import { type RealtimeChannel, type RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import { useDebounceFn } from '@vueuse/core'
import { defineStore } from 'pinia'
import { reactive } from 'vue'

import { captureException } from '@/sentry'
import supabase, { setMutationCallback } from '@/supabase'
import type { Callback, Table } from '@/types/supabase'

type Component = 'SignupsView' | 'UsersView' | 'UserFeedbackPage' | 'CarsPage' | 'CarDraftsPage' | 'DashboardPage' | 'MyNetworkPage' | 'DealersView' | 'RequestsView' | 'ConnectionsView' | 'TheSidebar' | 'BreadcrumbsBar' | 'CarDetailPage' | 'CarCommentsSection' | 'AssignCompanyModal' | 'CompanyManagementPage' | 'CompaniesPage' | 'App' | 'CompanyDetailPage'

const FALLBACK_TIMEOUT_MS = 1000

export const useRealtimeStore = defineStore('realtime', () => {
  const channels = reactive<Map<Table, { callbacks: { callback: Callback, component: Component }[], channel: RealtimeChannel }>>(new Map())
  const lastTimestamps = reactive<Map<Table, string>>(new Map())
  const debouncedCallbacks = new WeakMap<Callback, Callback>()
  const pendingExpectations = new Map<Table, ReturnType<typeof setTimeout>>()

  const getDebouncedCallback = (callback: Callback): Callback => {
    if (!debouncedCallbacks.has(callback)) {
      debouncedCallbacks.set(callback, useDebounceFn(callback, 50))
    }
    return debouncedCallbacks.get(callback)!
  }

  const clearExpectation = (table: Table): void => {
    const timer = pendingExpectations.get(table)
    if (timer) {
      clearTimeout(timer)
      pendingExpectations.delete(table)
    }
  }

  const expectEvent = (table: Table): void => {
    const channel = channels.get(table)
    if (!channel || channel.callbacks.length === 0) return

    clearExpectation(table)

    const timer = setTimeout(() => {
      pendingExpectations.delete(table)
      channels.get(table)?.callbacks.forEach(({ callback: cb }) => cb(table))
    }, FALLBACK_TIMEOUT_MS)

    pendingExpectations.set(table, timer)
  }

  const subscribe = (component: Component, tables: Table[], callback: Callback): void => {
    const debouncedCb = getDebouncedCallback(callback)
    tables.forEach(table => {
      const channel = channels.get(table)
      if (channel) {
        channel.callbacks.push({ callback: debouncedCb, component })
        return
      }

      const c = supabase.channel(`realtime-${table}`)
        .on('postgres_changes', { event: '*', schema: 'public', table }, (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          clearExpectation(table)
          const commitTimestamp = payload.commit_timestamp
          const lastTs = lastTimestamps.get(table)
          if (lastTs && commitTimestamp <= lastTs) return
          lastTimestamps.set(table, commitTimestamp)
          channels.get(table)?.callbacks.forEach(({ callback: cb }) => {
            cb(table)
          })
        })
        .subscribe((status, error) => {
          if (error) captureException(error)
          if (status !== 'SUBSCRIBED' && status !== 'CLOSED') captureException(error)
        })
      channels.set(table, { callbacks: [{ callback: debouncedCb, component }], channel: c })
    })
  }

  const unsubscribe = (component: Component): void => {
    const tables = Array.from(channels.keys())
      .filter(table => channels.get(table)?.callbacks.some(({ component: c }) => c === component))
    tables.forEach(table => {
      const channel = channels.get(table)!
      channel.callbacks = channel.callbacks.filter(({ component: c }) => c !== component)
      if (channel.callbacks.length === 0) {
        clearExpectation(table)
        channel.channel.unsubscribe()
          .catch(error => captureException(error))
          .finally(() => channels.delete(table))
      }
    })
  }

  const unsubscribeAll = async (): Promise<void> => {
    pendingExpectations.forEach(timer => clearTimeout(timer))
    pendingExpectations.clear()
    const unsubscribePromises = Array.from(channels.values())
      .map(({ channel }) => channel.unsubscribe().catch(error => captureException(error)))
    await Promise.all(unsubscribePromises)
      .finally(() => channels.clear())
  }

  setMutationCallback(expectEvent)

  return {
    subscribe,
    unsubscribe,
    unsubscribeAll
  }
})
